import { useState, useEffect } from 'react';
import axios, { endpoints } from 'src/utils/axios';
import { ProductRedeemFeeType } from 'src/type/product';
import { Pagination } from 'src/type/utils';
import { fParams, ParamsType } from 'src/utils/format-params';
import { omit } from 'lodash';
import { useBoolean } from './use-boolean';

export default function useProductFeeOptions(productId?: string, unitName?: string) {
  const loading = useBoolean(true);
  const [options, setOptions] = useState<
    { label: string; value: string; info: ProductRedeemFeeType }[]
  >([]);

  useEffect(() => {
    (async () => {
      try {
        if (!productId) {
          setOptions([]);
          return;
        }

        let fee = Array<ProductRedeemFeeType>();
        const firstChunk = await getProductRedeemFees({
          productId,
        });

        if (!firstChunk.results.length) {
          throw new Error('No products found');
        }

        fee = [...firstChunk.results];
        const paginateInfo = omit(firstChunk, 'results');

        for (let i = 1; i <= paginateInfo.totalPages; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const chunk = await getProductRedeemFees({
            lastDocId: fee[fee.length - 1].id,
            productId,
          });
          fee = [...fee, ...chunk.results];
        }

        const opt = fee?.map((f) => ({
          label: `${f.name} (${f.fee})`,
          value: f.id,
          info: f,
        }));

        setOptions(opt);
      } catch (error) {
        setOptions([]);
      } finally {
        loading.onFalse();
      }
    })();
  }, [productId]);

  const getProductRedeemFees = async (params?: ParamsType) => {
    const _params = {
      limit: 30,
      ...params,
      orderBy: 'weightGram:asc',
    };

    const { data } = await axios.get<Pagination<ProductRedeemFeeType>>(
      endpoints.productRedeemFees.get,
      {
        params: fParams(_params),
      }
    );
    return data;
  };

  return { productFeeOptions: options, isProductFeeOptionsLoading: loading.value };
}
